import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Observable, catchError, finalize, of } from 'rxjs';

import { IconName } from '../../../shared/components/icon/icon-data.generated';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { PatientSearchModal } from '../../../shared/components/patient-search-modal/patient-search-modal';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import { PatientFilter } from '../../../shared/models/patient-filter';
import {
  formatDobNumeric,
  formatLocalDate,
  formatLocalDateTime,
} from '../../../shared/utils/date-time/date-time.utils';
import { logHttpError } from '../../../shared/utils/logging/http-error-logging.utils';
import {
  formatEmailAddress,
  formatPatientMrn,
  getPatientInitials,
} from '../../../shared/utils/patient-display/patient-display.utils';
import { PatientAccessRecord } from '../../models/patient-access-record';
import {
  PatientPortalAccess,
  PatientPortalAccessBlockReasonType,
  PatientPortalAccessBlockReasonTypeId,
} from '../../models/patient-portal-access';
import {
  PATIENT_ACCESS_MINIMUM_CRITERIA,
  PatientAccessSearchService,
} from '../../services/patient-access-search.service';
import { PatientAccessSearchStateService } from '../../services/patient-access-search-state.service';
import { PatientPortalAccessService } from '../../services/patient-portal-access.service';
import { RevokeProxyAccessModal } from '../revoke-proxy-access-modal/revoke-proxy-access-modal';
import { AccessStatusBadge, getAccessStatusBadge } from '../../utils/access-status-badge.utils';
import {
  ProxyAgeOut,
  canRevokeProxy,
  describeProxyTimeline,
  isActiveLink,
  isProxyLink,
} from '../../utils/proxy-access.utils';

/**
 * Shown wherever a card renders a field nothing answers for. An em dash rather than a blank cell:
 * a blank reads as "this patient has none", the dash as "nothing to show".
 */
const NOT_AVAILABLE = '—';

/**
 * The chip on a live link, in the same treatment as the header's access status. A revoked link has
 * no chip: its inert "Revoked" button and muted row already say so.
 */
const ACTIVE_LINK_BADGE: AccessStatusBadge = { label: 'Active', class: 'badge--approved' };

/**
 * The chip that takes the place of "Active" on a grant that is ending, per {@link ProxyAgeOut}.
 * Null for a grant that isn't ending, which is most of them. Only an active link ever ages out, so
 * the chip it replaces is always "Active", and the age-out chip implies it.
 *
 * `soon` is amber: the link is still active, but "active" is not the only thing a staff member
 * needs to see on a grant that ends this month. `overdue` is red rather than amber because the
 * amber row resolves itself on a known date and this one does not — the job's catch-up window has
 * passed, so nothing will close that link but a staff member.
 */
const AGE_OUT_BADGES: Record<ProxyAgeOut, AccessStatusBadge | null> = {
  none: null,
  soon: { label: 'Age-Out Soon', class: 'badge--needs-info' },
  overdue: { label: 'Age-Out Overdue', class: 'badge--overdue' },
};

/** What a block or restore refused as a no-op means: someone else changed this patient first. */
const ACCESS_CHANGED_MESSAGE = "This patient's access changed since the page loaded.";

/** `pat.PatientPortalAccessBlockHistory.Notes` — the server rejects anything longer. */
export const BLOCK_COMMENT_MAX_LENGTH = 500;

/**
 * Why a block was not sent. The comment rule is worded twice, as PBI 59396 spells it: a line under
 * the field, and a sentence in the toast.
 */
const REASON_REQUIRED_MESSAGE = 'Please select a block reason.';
const COMMENT_REQUIRED_HINT = "A comment is required when selecting 'Other'";
const COMMENT_REQUIRED_MESSAGE = `${COMMENT_REQUIRED_HINT}.`;

/** One rendered row of the Proxy / Caregiver Access card. */
export interface ProxyRow {
  /** The `UserPatient` link id — what a revoke acts on, not the portal user's id. */
  id: number;
  displayName: string;
  /** "Guardian · Linked Jan 14, 2025". */
  meta: string;
  /** The bold tail of {@link meta} ("14 days"), or null when the line is plain throughout. */
  metaEmphasis: string | null;
  /** Where the grant stands relative to the patient turning 18. See {@link ProxyAgeOut}. */
  ageOut: ProxyAgeOut;
  /** The link is closed. The row is muted, has no chip, and keeps an inert "Revoked" button. */
  isRevoked: boolean;
  /**
   * The row's one chip: Active, or the age-out chip in place of Active. Null on a revoked link,
   * which shows the inert "Revoked" button instead.
   */
  statusLabel: string | null;
  statusClass: string | null;
  canRevoke: boolean;
  /** The revoke for this row is in flight. */
  isRevoking: boolean;
  /** Why this row's revoke failed, or null. */
  revokeError: string | null;
}

/**
 * How long the "Access restored" line rests under the switch before it goes back to describing
 * what the switch would do next. Long enough to be read by someone who has already looked away
 * from the control they flipped, short enough that it is gone before it could be mistaken for the
 * card's resting state.
 */
const RESTORE_CONFIRMATION_MS = 3000;

/** How long a toast stays up. */
const TOAST_MS = 4000;

/** A timestamp, or the dash. Absent is absent — it is never formatted into a plausible date. */
function formatOptionalDateTime(value: string | null | undefined): string {
  return value ? formatLocalDateTime(value) : NOT_AVAILABLE;
}

/** The three outcomes a write handler reacts to. See {@link PatientAccessDetail.runWrite}. */
interface WriteHandlers<T> {
  onSuccess: (result: T) => void;
  onError: (error: { status?: number }) => void;
  /** Runs once whichever way the write ends — where the in-flight flag is reset. */
  onSettled: () => void;
}

/**
 * Patient Access detail (Access & Controls → Search by Patient → a result's Manage Access).
 * The per-patient screen where staff review a patient's portal access, block or restore it, and
 * revoke a proxy's link.
 *
 * Two reads back the screen. `GET /Patients/{patientId}` returns identity only (name, DOB, MRN,
 * email). `GET /Patients/{patientId}/PortalAccess` returns the access state: whether a staff block
 * is in force, the block on file, and every portal account linked to the patient — the patient's
 * own (Self) account and revoked links included. The Proxy / Caregiver Access card is the proxy
 * subset of that list.
 *
 * **What still has no source is the login history**: last login, sessions in the trailing 30 days
 * and print/export events are on neither payload, so those rows stay dashed out and the card says
 * so. Nothing here invents a value — staff reading a patient's record must not be shown a
 * "Last login" that this screen made up.
 *
 * The Portal Access Control card **writes**, through a single switch. Turning it on sends
 * `PUT /Patients/{patientId}/PortalAccess/Block` with the chosen reason id; turning it off sends
 * `PUT .../PortalAccess/Restore`. Each answers the patient's access state as it now stands, and
 * that response replaces what the screen holds — the card never shows a change it inferred rather
 * than one the server confirmed, and the switch's own position is bound to that answer rather than
 * to where it was dragged. Which direction a flip means is decided by the state the server last
 * reported, so until the access read lands, and if it fails, the switch is locked: the screen
 * cannot know which of the two a flip would be, and each endpoint refuses the no-op the other
 * would be. See {@link accessUnavailableReason}.
 *
 * The Proxy / Caregiver Access card writes too: Revoke sends
 * `PUT .../PortalAccess/LinkedUsers/{userPatientId}/Revoke` behind a confirmation dialog, and
 * takes the refreshed access state from the response. It is confirmed rather than fired on the
 * first click because it is terminal and can disable the proxy's portal account outright — see
 * {@link revokeConfirmId}.
 */
@Component({
  selector: 'app-patient-access-detail',
  standalone: true,
  imports: [
    RouterLink,
    IconComponent,
    ProgressSpinnerComponent,
    PatientSearchModal,
    RevokeProxyAccessModal,
  ],
  templateUrl: './patient-access-detail.html',
  styleUrl: './patient-access-detail.scss',
})
export class PatientAccessDetail implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  /** Needed so the write handlers can register an `afterNextRender` hook outside an injection context. */
  private readonly injector = inject(Injector);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly searchService = inject(PatientAccessSearchService);
  private readonly searchStateService = inject(PatientAccessSearchStateService);
  private readonly portalAccessService = inject(PatientPortalAccessService);

  /**
   * Null when the route param isn't a positive integer — `:patientId` happily matches `abc`, and
   * a `GET /Patients/NaN` is a request worth never making.
   */
  readonly patientId = PatientAccessDetail.parsePatientId(
    this.route.snapshot.paramMap.get('patientId'),
  );

  readonly isLoading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly notFound = signal(false);
  readonly patient = signal<PatientAccessRecord | null>(null);
  readonly isModalOpen = signal(false);

  /**
   * The patient's portal access. `null` is "this didn't load", not "no access" — a patient nobody
   * has linked an account to is a loaded {@link PatientPortalAccess} with an empty `linkedUsers`,
   * which the cards say differently.
   *
   * It has no loading flag of its own. The screen is already gated on the patient fetch, which
   * runs first, and a second spinner inside the cards would be movement for its own sake.
   */
  readonly portalAccess = signal<PatientPortalAccess | null>(null);

  /**
   * True when the access read failed while the patient itself loaded. The cards say so rather than
   * falling silent: a staff member must not read an empty Proxy / Caregiver Access card as "nobody
   * holds access" when the truth is that the screen never found out.
   */
  readonly accessLoadFailed = signal(false);

  /**
   * The Block Reason options and the id the chosen one carries.
   *
   * The reason is held as an **id**, not as the label: that is what
   * `PUT .../PortalAccess/Block` takes, and the server validates it against its own enum. Null is
   * "nothing picked", which {@link onBlockPortalAccess} refuses to send — an unpicked select
   * would otherwise post `0` and come back a 400 with nothing on screen to explain it.
   *
   * Seeded from the block on file so the card opens on the reason already recorded rather than on
   * the placeholder.
   */
  readonly blockReasonTypes = signal<PatientPortalAccessBlockReasonType[]>([]);
  readonly blockReasonTypeId = signal<number | null>(null);

  /**
   * True when the reason lookup failed. Without a reason there is no block, so the card says why
   * rather than leaving staff an empty select and a switch that refuses them for no stated reason.
   */
  readonly reasonTypesLoadFailed = signal(false);

  /**
   * True once a block was attempted without a reason, so the select is marked invalid. The toast
   * says why; this ties the message to the control it sends the staff member back to. Cleared as
   * soon as a reason is picked.
   */
  readonly reasonRequiredShown = signal(false);

  /**
   * The staff comment sent as the block's `notes`. Optional, except when the reason is "Other" —
   * see {@link isCommentRequired}. Held as typed and trimmed only when sent.
   */
  readonly blockComment = signal('');
  readonly blockCommentMaxLength = BLOCK_COMMENT_MAX_LENGTH;

  /** In flight / failed state per action, so the card can say what happened. */
  readonly isBlocking = signal(false);
  readonly blockError = signal<string | null>(null);
  readonly isRestoring = signal(false);
  readonly restoreError = signal<string | null>(null);

  /**
   * Set when a block or restore came back refused as a no-op — someone else changed this patient
   * after the page loaded. Card-level rather than on the block or restore slot, because the
   * refreshed state it triggers usually swaps which of those two slots is on screen.
   */
  readonly accessChangedError = signal<string | null>(null);

  /**
   * Whether a restore that just landed is still being confirmed with an "Access restored" line.
   *
   * It has to be held rather than derived, because by then there is nothing left on the record to
   * derive it from: the block is gone and the patient looks like any other unblocked one. It
   * clears itself after {@link RESTORE_CONFIRMATION_MS} and the card returns to its normal layout.
   */
  readonly restoreConfirmed = signal(false);

  /**
   * The `UserPatient` link the row is asking staff to confirm a revoke on, the one currently in
   * flight, and the last failure.
   *
   * Revoke is confirmed in a dialog rather than fired on the first click, which is not the
   * pattern the other row actions use and is deliberate. The endpoint is terminal — re-granting
   * takes a whole new proxy verification request — and when this is the proxy's last active link
   * the API disables their portal account and Entra identity along with it. A single stray click
   * on a dense list should not be able to do that.
   *
   * One id rather than a set: the dialog asks about one caregiver, and it is what names them in
   * the question — see {@link revokeConfirmProxy}.
   */
  readonly revokeConfirmId = signal<number | null>(null);
  readonly revokingId = signal<number | null>(null);
  readonly revokeError = signal<{ userPatientId: number; message: string } | null>(null);

  /**
   * The row the confirmation dialog is open for, or null when it is closed. Resolved from
   * {@link revokeConfirmId} rather than held as a second signal, so the dialog cannot outlive the
   * link it is asking about: a refreshed list that no longer carries the id closes it.
   */
  readonly revokeConfirmProxy = computed<ProxyRow | null>(() => {
    const confirmingId = this.revokeConfirmId();
    return confirmingId === null
      ? null
      : (this.proxyRows().find((row) => row.id === confirmingId) ?? null);
  });

  /**
   * The confirmation that a write went through.
   *
   * A toast rather than a line on the row, because the row it would sit on has just changed
   * underneath the staff member: the row mutes and its button turns to "Revoked", and a message
   * in that space would be read as part of the row's new state rather than as an acknowledgement
   * of what they just did.
   */
  readonly toastMessage = signal<string | null>(null);

  /**
   * Whether the toast confirms a write or reports a block that was not sent. A refused submission
   * is a toast too, so staff see it in the same place whichever control they were looking at.
   */
  readonly toastVariant = signal<'success' | 'error'>('success');

  /** The search dialog gates on the same count the endpoint enforces (see the search service). */
  readonly minimumCriteria = PATIENT_ACCESS_MINIMUM_CRITERIA;
  readonly notAvailable = NOT_AVAILABLE;

  /**
   * True once a write's response has been applied, after which the initial access read is ignored
   * if it is still to land.
   *
   * Defence in depth rather than a live race: every write is gated on {@link hasPortalAccess}, so
   * none can be sent before that read has answered. The guard stays so a later retry or second
   * read can never roll the card back past a state a write has since confirmed.
   */
  private writeApplied = false;

  private toastTimeout: ReturnType<typeof setTimeout> | null = null;
  private restoreConfirmationTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.clearTimer(this.toastTimeout);
      this.clearTimer(this.restoreConfirmationTimeout);
    });
  }

  /**
   * Whether a staff block is in force. Read off the loaded access record rather than held
   * separately: the record is replaced with the endpoint's response after a block, so there is one
   * answer on screen and it is the server's.
   */
  readonly isBlocked = computed(() => this.portalAccess()?.isBlocked === true);

  /**
   * Whether the picked reason is "Other", which makes the comment required. Matched on the id,
   * which the API's enum names as its contract with the lookup table; the `name` and the label are
   * both database columns, so neither is guaranteed. Still checked against the lookup, so a reason
   * it doesn't carry is never treated as "Other".
   */
  readonly isCommentRequired = computed(() => {
    const reasonTypeId = this.blockReasonTypeId();
    return (
      reasonTypeId === PatientPortalAccessBlockReasonTypeId.Other &&
      this.blockReasonTypes().some(
        (reason) => reason.patientPortalAccessBlockReasonTypeId === reasonTypeId,
      )
    );
  });

  /**
   * "Other" with nothing but whitespace in the comment. Shown as soon as "Other" is picked rather
   * than on the first failed submit, so staff learn the field is required before they flip the
   * switch, and gone again the moment there is something to send.
   */
  readonly isCommentMissing = computed(
    () => this.isCommentRequired() && this.blockComment().trim() === '',
  );

  readonly commentRequiredHint = COMMENT_REQUIRED_HINT;

  readonly errorTitle = computed(() =>
    this.notFound() ? 'Patient not found' : 'Unable to load patient',
  );
  readonly errorIcon = computed<IconName>(() => (this.notFound() ? 'search' : 'warning'));

  readonly initials = computed(() => {
    const patient = this.patient();
    return patient ? getPatientInitials(patient.displayName) : '';
  });

  readonly dateOfBirth = computed(() => {
    const patient = this.patient();
    return patient ? formatDobNumeric(patient.dateOfBirth) : NOT_AVAILABLE;
  });

  readonly medicalRecordNumber = computed(() =>
    formatPatientMrn(this.patient()?.medicalRecordNumber),
  );

  readonly emailAddress = computed(() => formatEmailAddress(this.patient()?.emailAddress));

  /** "MRN: 7823011 · sthompson@example.com" under the patient's name. */
  readonly identitySummary = computed(
    () => `MRN: ${this.medicalRecordNumber()} · ${this.emailAddress()}`,
  );

  /** True once the access state has loaded at all — a patient with no linked accounts included. */
  readonly hasPortalAccess = computed(() => this.portalAccess() !== null);

  /**
   * Any write on the screen is in flight.
   *
   * All three actions answer with the patient's whole access state and replace what the screen
   * holds, so two of them overlapping means the slower response wins — a revoke landing after a
   * block would show the patient's links as the server saw them *before* the block. Guarding each
   * action on its own flag only stops a double-click on that one button; this stops the pair.
   */
  readonly isWriting = computed(
    () => this.isBlocking() || this.isRestoring() || this.revokingId() !== null,
  );

  /**
   * Why the Portal Access Control card cannot be acted on, or null once the access state is known.
   *
   * Block and Restore are mutually exclusive only on a state that has actually loaded: each
   * endpoint refuses the no-op the other would be. Until the read lands neither is known to be the
   * right one, and once it has *failed* the screen has no idea which it is — offering Block there
   * puts a guaranteed 400 under a staff member's cursor on a patient who is already blocked, and
   * reports it as a generic "please try again" they can retry forever.
   *
   * The two unsourced cases are told apart, because "still loading" and "we could not find out"
   * ask different things of the person reading the card.
   */
  readonly accessUnavailableReason = computed(() => {
    if (this.hasPortalAccess()) {
      return null;
    }
    return this.accessLoadFailed()
      ? "Portal access for this patient couldn't be loaded, so it can't be changed here. Reload the page to try again."
      : 'Checking this patient’s portal access…';
  });

  /** A block or a restore is in flight — what the switch itself is waiting on. */
  readonly isWritingAccess = computed(() => this.isBlocking() || this.isRestoring());

  /**
   * Whether the switch is locked outright (`disabled`).
   *
   * Until the access read lands the screen doesn't know whether a flip would block or restore, so
   * both directions wait on it. Turning it **on** also needs the reason lookup: a block without a
   * reason is a 400, and with the lookup failed there is no reason to pick.
   *
   * A missing *pick* does not lock it. Flipping without one goes down {@link onBlockPortalAccess}'s
   * validation path, which marks the select invalid and sends focus there — a dead control explains
   * nothing on its own.
   *
   * A write in flight is not a lock either: that is `aria-disabled` plus the {@link isWriting}
   * guard, so the switch keeps focus through the request instead of the browser blurring it.
   */
  readonly isToggleLocked = computed(
    () => !this.hasPortalAccess() || (!this.isBlocked() && this.reasonTypesLoadFailed()),
  );

  /**
   * The line under the switch. It carries the card's whole state: the restriction in force, the
   * progress of a write and the "Access restored" confirmation, since the switch is the only
   * control that changes access. The failed read keeps its wording short here and the full
   * explanation in the alert underneath, so the two don't say the same thing twice.
   */
  readonly accessSublabel = computed(() => {
    if (this.isBlocking()) {
      return 'Blocking portal access…';
    }
    if (this.isRestoring()) {
      return 'Restoring…';
    }
    if (this.isBlocked()) {
      return 'Access currently blocked';
    }
    // The confirmation rests here for a moment before the line goes back to describing what the
    // switch would do next.
    if (this.restoreConfirmed()) {
      return 'Access restored';
    }
    if (!this.hasPortalAccess()) {
      return this.accessLoadFailed()
        ? 'Access state unavailable'
        : 'Checking this patient’s portal access…';
    }
    if (this.reasonTypesLoadFailed()) {
      return 'Block reasons unavailable';
    }
    return 'Prevents all users from logging in';
  });

  /**
   * The header chip: one of the three access states, or a neutral "Status unavailable" until the
   * access read lands rather than borrowing the Active treatment on an unsourced guess. The same
   * helper the audit-log screens use, so one patient reads the same on both.
   */
  readonly accessStatusBadge = computed(() => getAccessStatusBadge(this.portalAccess()));

  /**
   * Only the unsourced chip needs explaining; a status that loaded speaks for itself. The two
   * unsourced cases are told apart, because "still loading" and "we could not find out" ask
   * different things of the person reading the chip.
   */
  readonly accessStatusTitle = computed(() => {
    if (this.hasPortalAccess()) {
      return null;
    }
    return this.accessLoadFailed()
      ? "Portal access for this patient couldn't be loaded."
      : 'Loading portal access…';
  });

  /** The block in force, or null when access isn't blocked. */
  readonly block = computed(() => this.portalAccess()?.block ?? null);

  readonly blockedOn = computed(() => formatOptionalDateTime(this.block()?.blockedOnUtc));
  readonly blockedBy = computed(() => this.block()?.blockedByDisplayName || NOT_AVAILABLE);
  /**
   * The acting admin's initials for the chip beside their name.
   *
   * Empty when the admin row couldn't be resolved, which the template takes as "no chip": the
   * placeholder the API sends in that case is not a name, and running it through the same initials
   * helper would put a confident monogram on nobody.
   */
  readonly blockedByInitials = computed(() => {
    const name = this.block()?.blockedByDisplayName;
    return name ? getPatientInitials(name) : '';
  });
  /** The reason on file, which a block recorded without one does not have. */
  readonly blockedReason = computed(
    () => this.block()?.patientPortalAccessBlockReasonType ?? NOT_AVAILABLE,
  );
  readonly blockNotes = computed(() => this.block()?.notes ?? null);

  /**
   * The page banner's "Blocked <when> by <admin> — <reason>" line. Each part is dropped rather
   * than dashed when the block doesn't carry it, so the sentence never reads "by —".
   */
  readonly blockBannerDetail = computed(() => {
    const block = this.block();
    if (!block) {
      return null;
    }
    const when = block.blockedOnUtc ? ` ${formatLocalDateTime(block.blockedOnUtc)}` : '';
    const who = block.blockedByDisplayName ? ` by ${block.blockedByDisplayName}` : '';
    const why = block.patientPortalAccessBlockReasonType
      ? ` — ${block.patientPortalAccessBlockReasonType}`
      : '';
    return `Blocked${when}${who}${why}`;
  });

  /**
   * When the patient's own portal account was linked — the Self row's `createdOnUtc`.
   *
   * Deliberately the Self link rather than the earliest link of any kind: a guardian linked in
   * 2019 to a patient who activated their own login last month would otherwise date the patient's
   * account to 2019. Dashed out when there is no Self link, because then there is no such account.
   */
  readonly accountCreated = computed(() => {
    const self = this.portalAccess()?.linkedUsers.find((user) => !isProxyLink(user));
    return self ? formatLocalDate(self.createdOnUtc) : NOT_AVAILABLE;
  });

  /**
   * The proxy subset of the linked accounts — the patient's own (Self) row is filtered out, since
   * this card is read to decide whether someone *else's* access should continue.
   *
   * The age-out treatment is driven by the patient in view, not by anything on the link: a proxy
   * link has no expiry date of its own, it simply lives until the patient turns 18 and the nightly
   * job revokes it. So the patient's date of birth is handed to each row — see
   * `describeProxyTimeline`.
   */
  readonly proxyRows = computed<ProxyRow[]>(() => {
    const revokingId = this.revokingId();
    const failure = this.revokeError();
    const dateOfBirth = this.patient()?.dateOfBirth;

    return (this.portalAccess()?.linkedUsers ?? []).filter(isProxyLink).map((user) => {
      // Keyed off the status *id* rather than the string beside it: the id is the contract with
      // `pat.UserPatientStatusType`, the label is free text on the wire.
      const isActive = isActiveLink(user);
      const timeline = describeProxyTimeline(user, dateOfBirth);
      // An ending grant swaps "Active" for its age-out chip; a revoked link has no chip.
      const status = AGE_OUT_BADGES[timeline.ageOut] ?? (isActive ? ACTIVE_LINK_BADGE : null);

      return {
        id: user.userPatientId,
        displayName: user.displayName,
        // Who they are to the patient, and where their access stands. An unnamed relationship is
        // dropped rather than printed as a leading separator with nothing before it — and read
        // defensively, because a null here would otherwise throw inside the computed.
        meta: [user.relationshipType, timeline.text].filter((part) => !!part?.trim()).join(' · '),
        metaEmphasis: timeline.emphasis,
        ageOut: timeline.ageOut,
        isRevoked: !isActive,
        statusLabel: status?.label ?? null,
        statusClass: status?.class ?? null,
        canRevoke: canRevokeProxy(user),
        isRevoking: revokingId === user.userPatientId,
        revokeError: failure?.userPatientId === user.userPatientId ? failure.message : null,
      };
    });
  });

  ngOnInit(): void {
    if (this.patientId === null) {
      this.isLoading.set(false);
      this.notFound.set(true);
      this.loadError.set(
        'That patient link is no longer valid. Search for the patient again to manage their access.',
      );
      return;
    }

    this.searchService
      .getPatientById(this.patientId)
      .pipe(
        catchError((error: { status?: number }) => {
          logHttpError('Failed to load patient for access management', error);
          const notFound = error?.status === 404;
          this.notFound.set(notFound);
          this.loadError.set(
            notFound
              ? 'This patient may have been removed, or the link you followed is no longer valid.'
              : 'Something went wrong while loading this patient. Please try again.',
          );
          this.isLoading.set(false);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((patient) => {
        if (!patient) {
          return;
        }

        this.patient.set(patient);
        this.loadError.set(null);
        this.notFound.set(false);
        this.isLoading.set(false);
        this.loadPortalAccess(patient.patientId);
      });
  }

  /**
   * Fetches the access state, after the patient has resolved rather than alongside it: a 404 on
   * the identity call means there is no patient to describe, and a second request for a patient
   * that doesn't exist is a request worth not making.
   *
   * A failure is logged and flagged to the cards rather than escalated to the page's error panel —
   * the identity the screen loaded successfully is still worth showing, and the cards say that
   * access couldn't be read rather than rendering as though it had come back empty.
   */
  private loadPortalAccess(patientId: number): void {
    this.portalAccessService
      .getPortalAccess(patientId)
      .pipe(
        catchError((error: unknown) => {
          logHttpError('Failed to load portal access for access management', error);
          this.accessLoadFailed.set(true);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((access) => {
        // A write that already answered holds the floor. See {@link writeApplied}.
        if (!access || this.writeApplied) {
          return;
        }

        this.accessLoadFailed.set(false);
        this.applyPortalAccess(access);
      });

    // Fetched alongside the access state rather than on first use: the select is on screen from
    // the first render, and populating it as staff reach for it is how a reason gets missed.
    this.portalAccessService
      .getBlockReasonTypes()
      .pipe(
        catchError((error: unknown) => {
          logHttpError('Failed to load portal access block reasons', error);
          this.reasonTypesLoadFailed.set(true);
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((types) => this.blockReasonTypes.set(types));
  }

  /**
   * Re-reads the access state after a write was refused as a no-op, so the card catches up with
   * whatever change beat it. A failure here leaves the card as it was; the message already on
   * screen still says it is out of date.
   */
  private refreshPortalAccess(patientId: number): void {
    this.portalAccessService
      .getPortalAccess(patientId)
      .pipe(
        catchError((error: unknown) => {
          logHttpError('Failed to refresh portal access after a conflicting change', error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((access) => {
        if (access) {
          this.applyWriteResult(access);
        }
      });
  }

  /**
   * Takes the server's account of this patient's access as the one on screen — after a load and
   * after a write alike, so there is never a moment where the card shows a block the UI inferred
   * rather than one the server confirmed.
   *
   * The reason select is reseeded only when the block state changes (the first load included) or
   * a block is in force. A revoke on an unblocked patient answers with `block: null`, and reseeding
   * from that would wipe a reason the staff member had just picked.
   *
   * The comment is cleared on a block state change only. It belongs to the block being drafted,
   * so once that block has landed (or someone else's has) it must not be waiting to be sent with
   * the next one.
   */
  private applyPortalAccess(access: PatientPortalAccess): void {
    const blockStateChanged = this.portalAccess()?.isBlocked !== access.isBlocked;
    this.portalAccess.set(access);
    if (blockStateChanged || access.isBlocked) {
      this.blockReasonTypeId.set(access.block?.patientPortalAccessBlockReasonTypeId ?? null);
    }
    if (blockStateChanged) {
      this.blockComment.set('');
      this.reasonRequiredShown.set(false);
    }
  }

  /**
   * Takes a **write's** account of this patient's access, and marks the initial read as overtaken
   * so a response composed before this one cannot roll it back. See {@link writeApplied}.
   */
  private applyWriteResult(access: PatientPortalAccess): void {
    this.writeApplied = true;
    this.accessLoadFailed.set(false);
    this.applyPortalAccess(access);
  }

  /**
   * Sends one write and routes its outcome. `finalize` resets the in-flight flag once, whichever
   * way the request ends, so no handler has to remember to.
   */
  private runWrite<T>(request$: Observable<T>, handlers: WriteHandlers<T>): void {
    request$
      .pipe(finalize(handlers.onSettled), takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: handlers.onSuccess, error: handlers.onError });
  }

  /** Raises a toast in the corner, then clears it. See {@link toastMessage}. */
  private showToast(message: string, variant: 'success' | 'error' = 'success'): void {
    this.toastVariant.set(variant);

    this.clearTimer(this.toastTimeout);
    this.toastTimeout = setTimeout(() => this.toastMessage.set(null), TOAST_MS);

    // A signal ignores an equal value, so the same refusal twice inside TOAST_MS would leave the
    // live region untouched and a screen reader silent. Empty it for one render, then raise it
    // again — unless another toast has taken its place in between.
    if (this.toastMessage() === message) {
      this.toastMessage.set(null);
      afterNextRender(
        () => {
          if (this.toastMessage() === null) {
            this.toastMessage.set(message);
          }
        },
        { injector: this.injector },
      );
      return;
    }

    this.toastMessage.set(message);
  }

  /**
   * Takes the "Access restored" confirmation off the switch.
   *
   * Called whenever the card changes underneath it as well as on its own timer: a confirmation is
   * a claim about the moment a restore happened, and a block or a revoke since has made it a claim
   * about a card that no longer exists.
   */
  private clearRestoreConfirmation(): void {
    this.clearTimer(this.restoreConfirmationTimeout);
    this.restoreConfirmationTimeout = null;
    this.restoreConfirmed.set(false);
  }

  private clearTimer(timer: ReturnType<typeof setTimeout> | null): void {
    if (timer !== null) {
      clearTimeout(timer);
    }
  }

  /**
   * The switch is the access control, in both directions: flipping it on blocks the patient,
   * flipping it off restores them. Which one this is comes from the state the server last
   * reported, never from where the input has just been dragged — the two are the same thing until
   * a write fails, and then only the first is true.
   *
   * Which is why the DOM state is put back before the request goes out. The input is bound to
   * {@link isBlocked}, which changes only once the server has said so, and Angular writes nothing
   * back to a binding whose value hasn't changed. Without this a refused block would leave a red
   * switch standing over a patient who can still sign in, and a refused restore would show a
   * blocked patient as free to log in. It also covers a flip while a write is in flight: the
   * switch is only `aria-disabled` then, and the handlers below refuse to send.
   */
  onPortalAccessToggle(event: Event): void {
    const wasBlocked = this.isBlocked();
    (event.target as HTMLInputElement).checked = wasBlocked;

    if (wasBlocked) {
      this.onRestorePortalAccess();
    } else {
      this.onBlockPortalAccess();
    }
  }

  /**
   * Blocks the patient out of the portal — `PUT /Patients/{patientId}/PortalAccess/Block`.
   *
   * Refuses to send without a reason: the server requires a valid reason id, so an unpicked select
   * would post `0` and come back a 400. Instead the select is marked invalid and given focus, so
   * the staff member lands on the thing that is missing. "Other" without a comment is refused the
   * same way, onto the comment field. The server would accept that one, so this is the only place
   * the rule is enforced. Both say why in a toast.
   *
   * The comment goes as `notes`, trimmed, or null when blank. The server records it on the block's
   * history row, which the audit log's Block entry points at.
   *
   * On success the response replaces the whole access record. It carries the new history row, its
   * timestamp and the acting admin, so patching a local flag instead would leave the card showing
   * a block with no provenance under it.
   *
   * Re-entry is guarded on {@link isWriting}: the endpoint refuses a second block with a 400, and
   * a double-click should not surface that as an error the staff member has to interpret.
   */
  onBlockPortalAccess(): void {
    const patientId = this.patientId;
    const reasonTypeId = this.blockReasonTypeId();

    // Never over an access state the screen hasn't got: on an already-blocked patient this is a
    // guaranteed 400 the staff member can only read as "it didn't work, try again".
    if (patientId === null || this.isWriting() || !this.hasPortalAccess()) {
      return;
    }

    if (reasonTypeId === null) {
      this.blockError.set(null);
      this.reasonRequiredShown.set(true);
      this.showToast(REASON_REQUIRED_MESSAGE, 'error');
      this.focusAfterRender('#block-reason');
      return;
    }

    if (this.isCommentMissing()) {
      this.blockError.set(null);
      this.showToast(COMMENT_REQUIRED_MESSAGE, 'error');
      this.focusAfterRender('#block-comment');
      return;
    }

    const notes = this.blockComment().trim() || null;

    this.isBlocking.set(true);
    this.blockError.set(null);
    this.accessChangedError.set(null);
    // The previous restore's outcome has nothing to say about the patient once they are blocked
    // again, and leaving "Access restored" on the card through a fresh block would read as a claim
    // about this one.
    this.clearRestoreConfirmation();
    this.restoreError.set(null);

    this.runWrite(
      this.portalAccessService.blockPortalAccess(patientId, {
        patientPortalAccessBlockReasonTypeId: reasonTypeId,
        notes,
      }),
      {
        onSuccess: (access) => {
          this.applyWriteResult(access);
          this.showToast('Portal access blocked.');
        },
        onError: (error) => {
          logHttpError('Failed to block portal access', error);
          if (error?.status === 400) {
            this.onAccessChanged(patientId);
            return;
          }
          this.blockError.set(
            error?.status === 404
              ? 'This patient could not be found. Search for them again and retry.'
              : "Portal access wasn't blocked. Please try again.",
          );
        },
        onSettled: () => this.isBlocking.set(false),
      },
    );
  }

  /**
   * Lifts the block — `PUT /Patients/{patientId}/PortalAccess/Restore`.
   *
   * No reason is asked for, because the endpoint doesn't take one: a restore is recorded against
   * the acting admin and the history row, and nothing else is required.
   *
   * The response carries two separate outcomes. The access state replaces what the screen holds,
   * as with a block. Whether the patient was *told* is reported apart from it, and only the toast
   * speaks to it — the server notifies after the commit, best-effort, so a restore nobody was
   * notified of is still a completed restore: the switch reports it either way, and
   * the toast simply stops short of claiming a notification that wasn't sent.
   */
  onRestorePortalAccess(): void {
    const patientId = this.patientId;

    // See the matching gate on {@link onBlockPortalAccess}: restoring a patient who isn't blocked
    // is a 400, and until the read lands the screen doesn't know which they are.
    if (patientId === null || this.isWriting() || !this.hasPortalAccess()) {
      return;
    }

    this.isRestoring.set(true);
    this.restoreError.set(null);
    this.accessChangedError.set(null);
    this.clearRestoreConfirmation();

    this.runWrite(this.portalAccessService.restorePortalAccess(patientId, { notes: null }), {
      onSuccess: (result) => {
        this.applyWriteResult(result.patientPortalAccess);

        // The confirmation rests under the switch while the toast carries the same news to the
        // corner, then both clear themselves.
        this.restoreConfirmed.set(true);
        this.restoreConfirmationTimeout = setTimeout(
          () => this.restoreConfirmed.set(false),
          RESTORE_CONFIRMATION_MS,
        );

        // Branched on the restore's own `isPatientNotified`, never on the call having succeeded:
        // the notification goes out after the commit and is best-effort, so a restore that did go
        // through can still come back unnotified. The toast reports the restore in both cases and
        // claims the notification in only one of them.
        this.showToast(
          result.isPatientNotified
            ? 'Portal access restored. Patient has been notified.'
            : 'Portal access restored.',
        );
      },
      onError: (error) => {
        logHttpError('Failed to restore portal access', error);
        if (error?.status === 400) {
          this.onAccessChanged(patientId);
          return;
        }
        this.restoreError.set(
          error?.status === 404
            ? 'This patient could not be found. Search for them again and retry.'
            : "Portal access wasn't restored. Please try again.",
        );
      },
      onSettled: () => this.isRestoring.set(false),
    });
  }

  /**
   * A 400 on block or restore is the endpoint refusing a no-op: someone else blocked or restored
   * this patient after the page loaded. Retrying would fail the same way every time, so the card
   * says what happened and re-reads the state to catch up.
   */
  private onAccessChanged(patientId: number): void {
    this.accessChangedError.set(ACCESS_CHANGED_MESSAGE);
    this.refreshPortalAccess(patientId);
  }

  /**
   * Opens the confirmation dialog. Nothing is sent — see {@link revokeConfirmId} for why this is
   * two steps rather than one.
   *
   * The dialog takes its own initial focus (onto Cancel), so nothing is moved here; the row is
   * left exactly as it was, which is what makes the return trip in
   * {@link onCancelRevokeProxy} a simple one.
   */
  onRevokeProxy(userPatientId: number): void {
    this.revokeError.set(null);
    this.revokeConfirmId.set(userPatientId);
  }

  /** Backs out of a revoke the staff member decided against, returning focus where it started. */
  onCancelRevokeProxy(): void {
    const cancelledId = this.revokeConfirmId();
    this.revokeConfirmId.set(null);

    if (cancelledId !== null) {
      this.focusAfterRender(`[data-revoke="${cancelledId}"]`);
    }
  }

  /**
   * Moves focus to the first match once the page has re-rendered. Scoped to this component's own
   * element, so a selector cannot reach a control on another part of the page.
   */
  private focusAfterRender(selector: string): void {
    afterNextRender(
      () => {
        const host = this.elementRef.nativeElement as HTMLElement;
        host.querySelector<HTMLElement>(selector)?.focus();
      },
      { injector: this.injector },
    );
  }

  /**
   * Withdraws one proxy's access — `PUT .../PortalAccess/LinkedUsers/{userPatientId}/Revoke`.
   *
   * The response is the patient's access state after the revoke, so the row turns to
   * Revoked because the **server** says it is, not because this screen assumed the call worked.
   *
   * Failures are reported on the row that failed rather than at the top of the card: with several
   * caregivers listed, a message floating above them does not say which one is still active.
   *
   * Either way focus goes back to the row's button — the inert "Revoked" after a success, "Revoke"
   * after a failure — so a keyboard user isn't dropped at the top of the page when the dialog
   * closes.
   */
  onConfirmRevokeProxy(userPatientId: number): void {
    const patientId = this.patientId;

    if (patientId === null || this.isWriting()) {
      return;
    }

    // Read before the write, because the response rewrites the row: the toast has to name the
    // caregiver whose access was withdrawn, and "which row was that" is a question the refreshed
    // list can no longer answer on its own.
    const proxyName = this.proxyRows().find((row) => row.id === userPatientId)?.displayName ?? null;
    const rowButton = `[data-revoke="${userPatientId}"]`;

    this.revokeConfirmId.set(null);
    this.revokingId.set(userPatientId);
    this.revokeError.set(null);
    // A restore's outcome is a claim about the moment it happened. The card has changed since, so
    // "Access restored" must not still be standing over a list this revoke has just rewritten.
    this.clearRestoreConfirmation();

    this.runWrite(this.portalAccessService.revokeProxyAccess(patientId, userPatientId), {
      onSuccess: (access) => {
        this.applyWriteResult(access);
        this.showToast(
          proxyName ? `Proxy access revoked for ${proxyName}.` : 'Proxy access revoked.',
        );
        this.focusAfterRender(rowButton);
      },
      onError: (error) => {
        logHttpError('Failed to revoke proxy access', error);
        this.revokeError.set({
          userPatientId,
          message:
            error?.status === 404
              ? 'This link is no longer on the patient. Reload to see the current list.'
              : error?.status === 400
                ? "Access wasn't revoked — it may have changed since this page loaded. Reload and try again."
                : "Access wasn't revoked. Please try again.",
        });
        this.focusAfterRender(rowButton);
      },
      onSettled: () => this.revokingId.set(null),
    });
  }

  /**
   * The select's values are reason ids as strings. An empty value is the placeholder, which maps
   * back to null — "nothing picked" — rather than to `0`, which the server would reject.
   */
  onBlockReasonChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.blockReasonTypeId.set(value === '' ? null : Number(value));

    // Clearing as soon as a reason is picked, so the message doesn't outlive the thing it was
    // complaining about.
    if (value !== '') {
      this.blockError.set(null);
      this.reasonRequiredShown.set(false);
    }
  }

  /** The comment is held as typed; trimming happens once, when the block is sent. */
  onBlockCommentInput(event: Event): void {
    this.blockComment.set((event.target as HTMLTextAreaElement).value);
  }

  openModal(): void {
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
  }

  /**
   * Searching from here replaces the patient in view, so it hands the criteria to the shared
   * state service and returns to the results table — the same route the dialog feeds from the
   * Access & Controls landing page, rather than a second results list living on this screen.
   */
  onPatientSearch(filter: PatientFilter): void {
    this.closeModal();
    this.searchStateService.setCriteria(filter);
    void this.router.navigate(['/access-controls/patients']);
  }

  /** Clear resets the dialog's own fields; this screen has nothing to reset alongside it. */
  onPatientSearchCleared(): void {
    // Intentionally empty — see the doc comment above.
  }

  /**
   * Plain decimal digits only, checked before `Number` sees the string.
   *
   * `Number` also accepts the other numeric literal forms, so `/patients/4e2` would quietly load
   * patient 400 and `/patients/0x10` patient 16 — a URL that names one patient and opens another's
   * record. Authorization is the server's, so this is a legibility failure rather than a way in,
   * but a staff member must be able to trust that the id in the address bar is the record on
   * screen.
   */
  private static parsePatientId(value: string | null): number | null {
    const trimmed = value?.trim() ?? '';
    if (!/^\d+$/.test(trimmed)) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
  }
}
